// =============================================================================
// GAME RULES PAGE - Game description and rules
// =============================================================================

import { motion } from 'framer-motion';
import { fadeInUp } from '../utils/animations';

interface GameRulesPageProps {
  onBack: () => void;
}

export function GameRulesPage({ onBack }: Readonly<GameRulesPageProps>) {
  return (
    <main
      className="min-h-screen flex flex-col items-center justify-start px-4 py-8 relative"
      role="main"
      aria-label="Опис та правила гри"
    >
      <motion.div
        className="relative z-10 w-full max-w-4xl"
        initial="hidden"
        animate="visible"
        variants={{
          hidden: { opacity: 0 },
          visible: {
            opacity: 1,
            transition: { staggerChildren: 0.15, delayChildren: 0.1 },
          },
        }}
      >
        {/* Header */}
        <motion.div variants={fadeInUp} className="mb-8">
          <button
            onClick={onBack}
            className="text-war-dim hover:text-war-primary text-sm mb-4 flex items-center gap-2 transition-colors"
            aria-label="Повернутися назад"
          >
            <span>←</span>
            <span className="tracking-wider uppercase">Назад</span>
          </button>
          <h1 className="text-3xl md:text-4xl font-bold text-war-bright tracking-wider uppercase">
            Опис та правила гри
          </h1>
        </motion.div>

        {/* Game Overview */}
        <motion.div variants={fadeInUp} className="glass-card p-6 md:p-8 mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-war-primary mb-4 tracking-wide uppercase">
            Про гру
          </h2>
          <div className="space-y-4 text-war-primary/80 text-sm md:text-base leading-relaxed">
            <p>
              <strong className="text-war-bright">Головнокомандувач</strong> —
              це текстова стратегічна симуляція, яка переносить вас у
              критичний період української історії: з 20 лютого 2022 року.
            </p>
            <p>
              Ви берете на себе роль головнокомандувача Збройних Сил України
              та приймаєте рішення, які впливають на хід подій під час
              початку повномасштабного вторгнення кацапів.
            </p>
            <p>
              Гра починається з реальних історичних подій, але ваші рішення
              можуть змінити хід альтернативної історії.
            </p>
            <p>
              У розділі «Додати власний збір або петицію» кожен може додати
              посилання на свій збір чи петицію. Система створить унікальне
              посилання на гру, яке варто поширювати серед інших.
            </p>
            <p>
              Гравця, який проходить сценарії за таким посиланням, гра час від
              часу закликатиме підтримати відповідний збір або петицію.
            </p>
          </div>
        </motion.div>

        {/* How to Play */}
        <motion.div variants={fadeInUp} className="glass-card p-6 md:p-8 mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-war-primary mb-4 tracking-wide uppercase">
            Як грати
          </h2>
          <div className="space-y-3 text-war-primary/80 text-sm md:text-base">
            <div className="flex items-start gap-3">
              <span className="text-war-bright font-mono text-lg">01.</span>
              <div>
                <p className="font-bold text-war-primary mb-1">
                  Читайте брифінги
                </p>
                <p className="text-sm">
                  Кожна ситуація супроводжується детальним описом обстановки
                  та контексту
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-war-bright font-mono text-lg">02.</span>
              <div>
                <p className="font-bold text-war-primary mb-1">
                  Приймайте рішення
                </p>
                <p className="text-sm">
                  Оберіть один з варіантів дій, кожен з яких має свої
                  наслідки
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-war-bright font-mono text-lg">03.</span>
              <div>
                <p className="font-bold text-war-primary mb-1">
                  Стежте за параметрами
                </p>
                <p className="text-sm">
                  Ваші рішення впливають на п'ять ключових показників
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-war-bright font-mono text-lg">04.</span>
              <div>
                <p className="font-bold text-war-primary mb-1">
                  Досягайте цілей
                </p>
                <p className="text-sm">
                  Пройдіть через 20 критичних рішень та дізнайтеся про
                  результати вашого управління
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Parameters Explanation */}
        <motion.div variants={fadeInUp} className="glass-card p-6 md:p-8 mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-war-primary mb-4 tracking-wide uppercase">
            Параметри управління
          </h2>
          <div className="space-y-4 text-war-primary/80 text-sm md:text-base">
            <div className="border-l-2 border-war-primary pl-4">
              <p className="font-bold text-war-bright mb-1">
                🪖 Військова сила
              </p>
              <p className="text-sm">
                Боєздатність та ефективність Збройних Сил України
              </p>
            </div>
            <div className="border-l-2 border-war-primary pl-4">
              <p className="font-bold text-war-bright mb-1">💰 Економіка</p>
              <p className="text-sm">
                Економічна стабільність та ресурси країни
              </p>
            </div>
            <div className="border-l-2 border-war-primary pl-4">
              <p className="font-bold text-war-bright mb-1">💪 Стійкість нації</p>
              <p className="text-sm">
                Моральний дух населення та воля до спротиву
              </p>
            </div>
            <div className="border-l-2 border-war-primary pl-4">
              <p className="font-bold text-war-bright mb-1">
                🤝 Підтримка Заходу
              </p>
              <p className="text-sm">
                Рівень міжнародної підтримки та допомоги
              </p>
            </div>
            <div className="border-l-2 border-war-primary pl-4">
              <p className="font-bold text-war-bright mb-1">🏛️ Довіра до влади</p>
              <p className="text-sm">
                Легітимність керівництва та довіра суспільства
              </p>
            </div>
          </div>
        </motion.div>

        {/* Game Modes */}
        <motion.div variants={fadeInUp} className="glass-card p-6 md:p-8 mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-war-primary mb-4 tracking-wide uppercase">
            Режими гри
          </h2>
          <div className="space-y-4 text-war-primary/80 text-sm md:text-base">
            <div className="bg-war-bg/50 border border-war-dim/30 p-4">
              <p className="font-bold text-war-bright mb-2">
                📖 Стандартний режим
              </p>
              <p className="text-sm">
                Детальні брифінги з повним контекстом ситуації та
                розгорнутими описами рішень. Рекомендується для першого
                проходження.
              </p>
            </div>
            <div className="bg-war-bg/50 border border-war-dim/30 p-4">
              <p className="font-bold text-war-bright mb-2">
                ⚡ Лаконічний режим
              </p>
              <p className="text-sm">
                Короткі тексти та швидкі рішення. Підходить для тих, хто
                вже знайомий з грою або хоче швидше пройти сценарій.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Important Notes */}
        <motion.div variants={fadeInUp} className="glass-card p-6 md:p-8 mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-war-amber mb-4 tracking-wide uppercase">
            Важливо
          </h2>
          <div className="space-y-3 text-war-primary/80 text-sm md:text-base">
            <p>
              • Гра базується на реальних історичних та вигаданих подіях та може містити
              емоційно важкий контент
            </p>
            <p>
              • Ваш прогрес автоматично зберігається в браузері
            </p>
            <p>
              • Сценарії генеруються за допомогою штучного інтелекту на
              основі історичного контексту
            </p>
            <p>
              • Кожне рішення має наслідки — обирайте обережно
            </p>
            <p>
              • Якщо ви додали власний збір або петицію, поширюйте унікальне
              посилання на гру — саме за ним гравці бачитимуть заклики підтримки
            </p>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div
          variants={fadeInUp}
          className="text-center text-[10px] text-war-dim/50 font-mono tracking-widest uppercase"
        >
          Грайте відповідально. Кожне рішення має наслідки.
        </motion.div>
      </motion.div>
    </main>
  );
}
